import React from "react";
import { useMutation } from "@apollo/client";
import * as moment from "moment-timezone";

import { StreamProvider } from "@teamrota/shiftstreamlib";
import { RotaButton } from "@teamrota/rota-design";

import Modal from "~/src/components/modal";
import Tabs from "~/src/containers/modals/components/tabs";
import { formatDateMonthTimeShort } from "~/src/utils/formatting";
import { COLORS } from "~/src/styles/config.style";

import {
  ActionHelpText,
  ActionRow,
  Content,
  DetailsSurround,
  DetailsTable,
  Footer,
  MetadataDiv,
  Subtitle,
  Title,
} from "./styles";

import { shiftStreamClient } from "../../../graphql/client";
import { Provider } from "../../../components/provider";
import { ReasonPanel, StreamPanel } from "../../../components/stream";
import { useAccounts } from "../../../graphql/use-accounts";
import { getEmailStatus } from "../../../utils";

import StreamInfoModal from "../../streams/modal";

import { useEmail } from "../graphql/use-email";

import { EMAIL_ALGO } from "../graphql/mutation/email-algo";
import { EMAIL_CANCEL } from "../graphql/mutation/email-cancel";
import { EMAIL_CLOSE } from "../graphql/mutation/email-close";
import { EMAIL_EXPIRE } from "../graphql/mutation/email-expire";
import { EMAIL_RERUN } from "../graphql/mutation/email-rerun";
import { EMAIL_STOP } from "../graphql/mutation/email-stop";
import { CancelModal } from "../../../components/cancel";

interface EmailInfoModalProps {
  emailId: any | null;
  onClose: () => void;
}

const EmailInfoModal: React.FC<EmailInfoModalProps> = ({
  emailId,
  onClose,
}) => {
  if (!emailId) {
    return null;
  }

  const { email } = useEmail(parseInt(emailId));
  const { accounts } = useAccounts();

  if (!email) {
    return null;
  }

  // locate account for inbox
  let account = null;
  if (accounts) {
    account = accounts.find(
      (a: any) =>
        a.url === email.mailboxAddress && a.provider === StreamProvider.EMAIL,
    );
  }

  if (!account) {
    return null;
  }

  const modalTabs: Array<{ name: string; content: React.ReactNode }> = [
    {
      name: "Overview",
      content: <OverviewTab email={email} />,
    },
    {
      name: "Contents",
      content: <ContentsTab email={email} />,
    },
    {
      name: "Streams",
      content: <StreamsTab email={email} />,
    },
    {
      name: "Metadata",
      content: <MetadataTab email={email} />,
    },
  ];

  return (
    <Modal
      isOpen={Boolean(email)}
      isLoading={false}
      isOverlay={false}
      isLightOverlay={false}
      hasBtn={false}
      padding={0}
      background="white"
      onClose={onClose}
    >
      <Content>
        <Title>Email #{email.id}</Title>

        <Subtitle>
          <Provider account={account} />
        </Subtitle>

        <Tabs
          className="stream-info-tabs"
          defaultTab="Overview"
          tabs={modalTabs as any}
        />
      </Content>
    </Modal>
  );
};

const OverviewTab = ({ email }: { email: any }) => {
  const [algo, { loading: isAlgoLoading }] = useMutation(EMAIL_ALGO, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [cancel, { loading: isCancelLoading }] = useMutation(EMAIL_CANCEL, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [close, { loading: isCloseLoading }] = useMutation(EMAIL_CLOSE, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [expire, { loading: isExpireLoading }] = useMutation(EMAIL_EXPIRE, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [reRun, { loading: isReRunLoading }] = useMutation(EMAIL_RERUN, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [stop, { loading: isStopLoading }] = useMutation(EMAIL_STOP, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const [confirmingCancelId, setConfirmingCancelId] = React.useState<
    any | null
  >(null);

  const id = parseInt(email.id);

  const isExpired =
    email.isExpiredManually || moment(email.expiresAt).isBefore();

  const isAlgo = email.streams.find((stream: any) => !stream.noAlgo);

  return (
    <>
      <DetailsSurround>
        <DetailsTable>
          <tr>
            <th>ID</th>
            <td>#{email.id}</td>
          </tr>
          <tr>
            <th>From</th>
            <td>{email.sender}</td>
          </tr>
          <tr>
            <th>To</th>
            <td>{email.mailboxAddress}</td>
          </tr>
          <tr>
            <th>Subject</th>
            <td>{email.subject}</td>
          </tr>
          <tr>
            <th>Received</th>
            <td>
              {email.receivedAt
                ? formatDateMonthTimeShort(email.receivedAt)
                : "—"}
            </td>
          </tr>
          <tr>
            <th>Expiry</th>
            <td>
              {isExpired ? (
                <span style={{ color: COLORS.RED }}>
                  {formatDateMonthTimeShort(email.expiresAt)}
                  {email.isExpiredManually && " (brought forward manually)"}
                </span>
              ) : (
                <span>{formatDateMonthTimeShort(email.expiresAt)}</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Status</th>
            <td>{getEmailStatus(email)}</td>
          </tr>
        </DetailsTable>

        <Footer>
          <ActionRow>
            <RotaButton
              onClick={() =>
                stop({ variables: { id, stopped: !email.stopped } })
              }
              disabled={isExpired}
              size="large"
              variant={email.stopped ? "outlined" : "contained"}
              isLoading={isStopLoading}
            >
              {email.stopped ? "Resume Replies" : "Stop Replies"}
            </RotaButton>
            <ActionHelpText>
              <b>Temporarily stops all replies</b> from being sent for this
              email. Algorithm continues as usual.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => !isExpired && reRun({ variables: { id } })}
              disabled={isExpired}
              size="large"
              isLoading={isReRunLoading}
            >
              Re-Run Algo
            </RotaButton>
            <ActionHelpText>
              <b>Schedules the algorithm</b> on all streams to fill slots with
              any available candidates.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => algo({ variables: { id, algo: !isAlgo } })}
              size="large"
              variant={isAlgo ? "contained" : "outlined"}
              disabled={isExpired}
              isLoading={isAlgoLoading}
            >
              {isAlgo ? "Stop Algo" : "Resume Algo"}
            </RotaButton>
            <ActionHelpText>
              <b>Stops any further algorithm runs</b> on all streams for this
              email, even if slots become available.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => expire({ variables: { id } })}
              size="large"
              variant="contained"
              disabled={isExpired}
              isLoading={isExpireLoading}
            >
              {isExpired ? "Expired" : "Expire Now"}
            </RotaButton>
            <ActionHelpText>
              <b>Brings expiry forward to now</b>, which will send the expiry
              email and close any unfilled slots.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => close({ variables: { id } })}
              size="large"
              disabled={isExpired}
              isLoading={isCloseLoading}
            >
              Close Email
            </RotaButton>
            <ActionHelpText>
              <b>Closes this email</b>, removing unfilled slots, expiry will
              still occur at the normal time. <br />
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => setConfirmingCancelId(id)}
              size="large"
              isLoading={isCancelLoading}
            >
              Cancel All
            </RotaButton>
            <ActionHelpText>
              <b>Cancels this email's streams</b>, and associated shifts and
              bookings. Candidates will be notified.
            </ActionHelpText>
          </ActionRow>
        </Footer>
      </DetailsSurround>

      {confirmingCancelId && (
        <CancelModal
          streams={email.streams}
          isLoading={isCancelLoading}
          onProceed={async () => {
            await cancel({ variables: { id: confirmingCancelId } });
            setConfirmingCancelId(null);
          }}
          onClose={() => setConfirmingCancelId(null)}
        />
      )}
    </>
  );
};

const ContentsTab = ({ email }: { email: any }) => {
  if (email.state === "SKIPPED") {
    return (
      <>
        <br />
        <Subtitle>Email is {getEmailStatus(email)}</Subtitle>
      </>
    );
  }

  return (
    <>
      <DetailsSurround>
        <DetailsTable>
          <tr>
            <th>ID</th>
            <td>#{email.id}</td>
          </tr>
          <tr>
            <th>From</th>
            <td>{email.sender}</td>
          </tr>
          <tr>
            <th>To</th>
            <td>{email.mailboxAddress}</td>
          </tr>
          <tr>
            <th>Subject</th>
            <td>{email.subject}</td>
          </tr>
          <tr>
            <th>Received</th>
            <td>
              {email.receivedAt
                ? formatDateMonthTimeShort(email.receivedAt)
                : "—"}
            </td>
          </tr>
        </DetailsTable>

        <iframe
          style={{ width: "50vw", height: "40vh" }}
          src={email.preview}
          sandbox=""
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      </DetailsSurround>

      <div></div>
    </>
  );
};

const MetadataTab = ({ email }: { email: any }) => {
  return <MetadataDiv>{JSON.stringify(email.metadata, null, 2)}</MetadataDiv>;
};

const StreamsTab = ({ email }: { email: any }) => {
  const [selectedStreamId, setSelectedStreamId] = React.useState<any | null>(
    null,
  );

  const noStreams =
    (!email.scrapeReasons && !email.streams) ||
    (email.streams.length === 0 && email.scrapeReasons.length === 0);

  return (
    <div style={{ height: "60vh", overflowY: "scroll" }}>
      {noStreams && (
        <>
          <br />
          <Subtitle>No streams for this email</Subtitle>
        </>
      )}

      {email.scrapeReasons &&
        email.scrapeReasons.map((reason: string) => (
          <ReasonPanel reason={reason} showMenu={true} />
        ))}

      {email.streams &&
        email.streams.map((stream: any) => (
          <StreamPanel
            stream={stream}
            showMenu={true}
            onClick={() => setSelectedStreamId(stream.id)}
          />
        ))}

      <StreamInfoModal
        streamId={selectedStreamId}
        onClose={() => setSelectedStreamId(null)}
      />
    </div>
  );
};

export default EmailInfoModal;
